import React, { useState, useCallback } from 'react';
import { Form, Button, Typography, Card, Alert, Grid } from 'antd';
import { EnvironmentOutlined } from '@ant-design/icons';
import { SelfRegistrationSessionDTO } from '@/shared/api/model';
import SelfRegistrationLocationSelector from '../SelfRegistrationLocationSelector';
import { useSelfRegistration } from '../../hooks/useSelfRegistration';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

interface LocationSelectionStepProps {
    session: SelfRegistrationSessionDTO;
    onComplete: (session: SelfRegistrationSessionDTO) => void;
    onError: (message: string) => void;
}

interface LocationFormData {
    locationSelections: {
        zone?: any;
        region?: any;
        district?: any;
        ward?: any;
        street?: any;
        fullPath?: string;
    };
    gpsCoordinates: {
        latitude?: number;
        longitude?: number;
        accuracy?: number;
    };
    houseNumber: string;
    postalCode: string;
    isPrimary: boolean;
    validationErrors?: string[];
}

const LocationSelectionStep: React.FC<LocationSelectionStepProps> = ({
    session,
    onComplete,
    onError
}) => {
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const [formData, setFormData] = useState<LocationFormData>({
        locationSelections: {},
        gpsCoordinates: {},
        houseNumber: "",
        postalCode: "",
        isPrimary: true // Default to primary address for self-registration
    });
    const [validationResult, setValidationResult] = useState<{valid: boolean, errors: string[]} | null>(null);
    const { updateLocationInfo, isUpdatingLocationInfo } = useSelfRegistration();

    // Handle data from ConfigurableLocationSelector
    const handleLocationDataChange = useCallback((locationData: any) => {
        console.log('LocationSelectionStep - handleLocationDataChange received:', locationData);

        // Update form data
        setFormData(prev => ({
            ...prev,
            ...locationData
        }));

        // Update validation result if provided
        if (locationData.validationErrors !== undefined) {
            console.log('LocationSelectionStep - validation errors received:', locationData.validationErrors);

            setValidationResult({
                valid: locationData.validationErrors.length === 0,
                errors: locationData.validationErrors
            });
        }
    }, []);

    const handleSubmit = async () => {
        try {
            // Check if there are validation errors
            if (validationResult && !validationResult.valid) {
                console.log('Validation errors exist, cannot submit:', validationResult.errors);
                onError('Please fix the validation errors before continuing');
                return;
            }

            // Prepare location data for JSON serialization
            const locationDataJson = JSON.stringify({
                locationSelections: formData.locationSelections,
                gpsCoordinates: formData.gpsCoordinates,
                houseNumber: formData.houseNumber,
                postalCode: formData.postalCode,
                isPrimary: formData.isPrimary,
                addressType: "WORK_PLACE" // Use WORK_PLACE address type as configured for CUSTOMER entity
            });

            console.log('🚀 Submitting location data:', locationDataJson);

            // ✅ Await the API call and handle the response
            const updatedSession = await updateLocationInfo(session.sessionToken!, locationDataJson);
            
            console.log('✅ Location info updated, next step:', updatedSession.currentStep);
            
            // ✅ Trigger progression to next step
            onComplete(updatedSession);

        } catch (error: any) {
            console.error('Location submission failed:', error);
            onError(error.message || 'Failed to save location information');
        }
    };

    // Parse existing location data if available
    React.useEffect(() => {
        if (session?.locationDataJson) {
            try {
                const existingLocationData = JSON.parse(session.locationDataJson);
                setFormData(prev => ({
                    ...prev,
                    ...existingLocationData
                }));
            } catch (error) {
                console.warn('Failed to parse existing location data:', error);
            }
        }
    }, [session]);

    return (
        <div>
            <div style={{ textAlign: 'center', marginBottom: isMobile ? '20px' : '30px' }}>
                <EnvironmentOutlined style={{ 
                    fontSize: isMobile ? '36px' : '48px', 
                    color: '#1890ff', 
                    marginBottom: isMobile ? '12px' : '16px' 
                }} />
                <Title level={isMobile ? 4 : 3}>Location Information</Title>
                <Text type="secondary" style={{ 
                    fontSize: isMobile ? '14px' : '16px',
                    display: 'block'
                }}>
                    Please provide your work place address details
                </Text>
            </div>

            <Card 
                title="Work Place Address" 
                style={{ marginBottom: isMobile ? '16px' : '20px' }}
                size={isMobile ? 'small' : 'default'}
            >
                <div className="space-y-4">
                    <SelfRegistrationLocationSelector
                        addressType="WORK_PLACE"
                        initialValues={{
                            locationSelections: formData.locationSelections,
                            houseNumber: formData.houseNumber,
                            postalCode: formData.postalCode,
                            isPrimary: formData.isPrimary
                        }}
                        onLocationSelected={handleLocationDataChange}
                        disabled={false}
                    />
                </div>

                {validationResult && !validationResult.valid && (
                    <Alert
                        message="Validation Errors"
                        description={
                            <ul style={{ 
                                margin: 0, 
                                paddingLeft: isMobile ? '16px' : '20px',
                                fontSize: isMobile ? '13px' : '14px'
                            }}>
                                {validationResult.errors.map((error: string, index: number) => (
                                    <li key={index}>{error}</li>
                                ))}
                            </ul>
                        }
                        type="error"
                        showIcon
                        // @ts-ignore
                        size={isMobile ? 'small' : 'default'}
                        style={{ marginTop: isMobile ? '12px' : '16px' }}
                    />
                )}
            </Card>

            <Form.Item>
                <Button
                    type="primary"
                    onClick={handleSubmit}
                    loading={isUpdatingLocationInfo}
                    disabled={validationResult ? !validationResult.valid : false}
                    block
                    size={isMobile ? 'middle' : 'large'}
                    style={{ 
                        height: isMobile ? '44px' : '48px',
                        fontSize: isMobile ? '16px' : '18px'
                    }}
                >
                    Continue to Document Upload
                </Button>
            </Form.Item>
        </div>
    );
};

export default LocationSelectionStep;
